import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { TableHeadComponent } from "../../../common/TableHead";
import { Autocomplete, TextField } from "@mui/material";
import { Dispatcher } from "./SiteAboutPageUtils/siteAboutInterfaces";
import { WebhookAuthFields } from "../../../common/WebhookAuthFields";
import {
  WebhookAuthField,
  WebhookAuthValues,
  WebhookFieldErrors,
  applyWebhookAuthFieldToPatch,
  pickWebhookAuthValues,
} from "../../../utils/webhookAuth";
import { WEBHOOKS_TAB_TITLES } from "./WebhooksTab";
import {
  WebhookFieldErrorsById,
  WebhooksPatchMap,
} from "./SiteAboutPageUtils/siteWebhooks";

const capitalize = (value: string | null | undefined) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

/** One editable row. Its edits land under its own id in the patch map. */
const WebhookEditRow = ({
  webhook,
  setWebhooksPatchObject,
  setWebhooksChangesMade,
  fieldErrors = {},
}: {
  webhook: any;
  setWebhooksPatchObject: Dispatcher<WebhooksPatchMap>;
  setWebhooksChangesMade: Dispatcher<boolean>;
  fieldErrors?: WebhookFieldErrors;
}) => {
  const [method, setMethod] = React.useState<string>(webhook.method);

  // What the server returned (secrets possibly masked). Only fields that differ
  // from this go into the PATCH, so an untouched mask is never sent back.
  const savedAuthValues = React.useMemo(
    () => pickWebhookAuthValues(webhook),
    [webhook]
  );
  const [authValues, setAuthValues] =
    React.useState<WebhookAuthValues>(savedAuthValues);

  const patchField = (field: string, value: any) => {
    setWebhooksPatchObject((previousState: WebhooksPatchMap) => ({
      ...previousState,
      [webhook.id]: { ...(previousState?.[webhook.id] ?? {}), [field]: value },
    }));
    setWebhooksChangesMade(true);
  };

  const handleAuthFieldChange = (
    field: WebhookAuthField,
    value: string | null
  ) => {
    setAuthValues((previousState) => ({ ...previousState, [field]: value }));
    setWebhooksPatchObject((previousState: WebhooksPatchMap) => ({
      ...previousState,
      [webhook.id]: applyWebhookAuthFieldToPatch(
        previousState?.[webhook.id] ?? {},
        field,
        value,
        savedAuthValues[field]
      ),
    }));
    setWebhooksChangesMade(true);
  };

  return (
    <TableRow data-testid={`site-webhook-edit-row-${webhook.id}`}>
      <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
        <TextField
          defaultValue={webhook.display_name}
          variant="standard"
          sx={{ width: 200 }}
          type="string"
          error={Boolean(fieldErrors.display_name)}
          helperText={fieldErrors.display_name}
          inputProps={{ "aria-label": "Display Name" }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue = event.target.value;
            patchField("display_name", nextValue);
          }}
        />
      </TableCell>
      <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
        {capitalize(webhook.webhook_type)}
      </TableCell>
      <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
        <Autocomplete
          value={method}
          options={["GET", "POST"]}
          slotProps={{
            popper: {
              placement: "bottom-start",
            },
          }}
          onChange={(event, value: any) => {
            setMethod(value);
            patchField("method", value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Method"
              autoComplete="off"
              variant="standard"
              sx={{ width: 120 }}
            />
          )}
        />
      </TableCell>
      <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
        <TextField
          defaultValue={webhook.target_url}
          variant="standard"
          sx={{ width: 350 }}
          type="string"
          error={Boolean(fieldErrors.target_url)}
          helperText={fieldErrors.target_url}
          inputProps={{ "aria-label": "Target URL" }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue = event.target.value;
            patchField("target_url", nextValue);
          }}
        />
      </TableCell>
      <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
        <TextField
          multiline
          rows={4}
          defaultValue={webhook.message_body}
          variant="outlined"
          sx={{ width: 400 }}
          type="string"
          error={Boolean(fieldErrors.message_body)}
          helperText={fieldErrors.message_body || "*optional"}
          inputProps={{ "aria-label": "Message Body" }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue = event.target.value;
            patchField("message_body", nextValue);
          }}
        />
      </TableCell>
      <TableCell align="left" sx={{ fontFamily: "Mulish" }}>
        <WebhookAuthFields
          values={authValues}
          errors={fieldErrors}
          onFieldChange={handleAuthFieldChange}
          width={320}
          compact
        />
      </TableCell>
    </TableRow>
  );
};

/**
 * Site About → Webhooks in edit mode: one editable row per site webhook
 * (ENG-2233). Pending edits are keyed by webhook id so Save Changes can
 * PATCH each changed webhook on its own.
 */
const WebhooksTabEditMode = ({
  webhooksInfo,
  setWebhooksPatchObject,
  setWebhooksChangesMade,
  fieldErrorsById = {},
}: {
  webhooksInfo: any;
  setWebhooksPatchObject: Dispatcher<WebhooksPatchMap>;
  setWebhooksChangesMade: Dispatcher<boolean>;
  // Server 400 messages per webhook id, shown inline after a rejected save.
  fieldErrorsById?: WebhookFieldErrorsById;
}) => {
  const webhooks: any[] = Array.isArray(webhooksInfo) ? webhooksInfo : [];

  return (
    <TableContainer component={Paper}>
      <Table
        sx={{ minWidth: 1000, fontFamily: "Mulish" }}
        aria-label="simple table"
      >
        <TableHeadComponent titlesArray={WEBHOOKS_TAB_TITLES} />
        <TableBody>
          {webhooks.map((webhook: any) => (
            <WebhookEditRow
              key={webhook.id}
              webhook={webhook}
              setWebhooksPatchObject={setWebhooksPatchObject}
              setWebhooksChangesMade={setWebhooksChangesMade}
              fieldErrors={fieldErrorsById[webhook.id]}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default WebhooksTabEditMode;
